'use client';

import { AlertTriangle, LifeBuoy, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

interface ErrorSurfaceProps {
  title: string;
  message: string;
  error?: Error & { digest?: string };
  onRetry?: () => void;
  retryLabel?: string;
  fullScreen?: boolean;
}

export function ErrorSurface({
  title,
  message,
  error,
  onRetry,
  retryLabel = 'Erneut versuchen',
  fullScreen = false,
}: ErrorSurfaceProps) {
  const { t } = useI18n();

  return (
    <div
      className={cn(
        'flex items-center justify-center px-4 py-10',
        fullScreen ? 'min-h-screen bg-background' : 'min-h-[420px]'
      )}
    >
      <div className="relative w-full max-w-2xl overflow-hidden rounded-[28px] border border-border/70 bg-card/95 shadow-[0_30px_80px_rgba(0,0,0,0.18)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(239,68,68,0.14),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(245,158,11,0.12),transparent_38%)]" />
        <div className="relative space-y-6 p-8 md:p-10">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-3 text-red-300">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/80">
                <LifeBuoy className="h-3.5 w-3.5" />
                {t('Wiederherstellung aktiv')}
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-[2rem]">
                {t(title)}
              </h1>
              <p className="max-w-xl text-sm leading-7 text-muted-foreground/85 md:text-[15px]">
                {t(message)}
              </p>
            </div>
          </div>

          {error?.message && (
            <div className="rounded-2xl border border-border/60 bg-background/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
                {t('Technischer Hinweis')}
              </p>
              <p className="mt-2 text-sm leading-6 text-foreground/90">{error.message}</p>
              {error.digest && (
                <p className="mt-3 text-xs text-muted-foreground/70">Digest: {error.digest}</p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            {onRetry && (
              <Button onClick={onRetry} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                {t(retryLabel)}
              </Button>
            )}
            <Button variant="outline" onClick={() => window.location.reload()}>
              {t('Seite neu laden')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}